/**
 * Cliente HTTP del Worker — solo para los endpoints REST que tRPC no
 * cubre (login, refresh, logout). Para todo lo demás (catálogo, pedidos,
 * caja, pagos) usamos el cliente tRPC de `./trpc.ts`.
 */

import {
  clearSession,
  loadSession,
  saveSession,
  type PosSession,
} from '../auth/session.js';

export const WORKER_URL =
  (import.meta.env['VITE_WORKER_URL'] as string | undefined) ??
  'http://localhost:8787';

interface RefreshResponse {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
}

/**
 * Dedupe de refresh concurrente: si 5 queries tRPC disparan a la vez y
 * todas reciben 401, no queremos 5 refresh requests (el worker rota el
 * refresh token en cada llamada → las 4 últimas fallarían). Compartimos
 * la misma promesa.
 */
let refreshInFlight: Promise<PosSession> | null = null;

const doRefresh = async (session: PosSession): Promise<PosSession> => {
  const res = await fetch(`${WORKER_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });
  if (!res.ok) throw new ApiError('refresh_failed', res.status);
  const body = (await res.json()) as RefreshResponse;
  const next: PosSession = {
    ...session,
    accessToken: body.accessToken,
    accessExpiresAt: body.accessExpiresAt,
    refreshToken: body.refreshToken,
    savedAt: Date.now(),
  };
  saveSession(next);
  return next;
};

export const refresh = async (session: PosSession): Promise<PosSession> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh(session).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Fallback global cuando el refresh falla (token revocado, expirado largo,
 * red sin respuesta). Limpia sesión y recarga para que AuthGate muestre
 * el login otra vez. Se llama desde dos sitios (apiFetch y el tRPC link).
 */
export const handleAuthFailure = (): void => {
  clearSession();
  // Recarga solo si estamos en browser — evita romper SSR/tests.
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    public detail?: unknown,
  ) {
    super(`api_error:${code} (${status})`);
  }
}

const sendOnce = async (
  path: string,
  init: RequestInit,
  token?: string,
): Promise<Response> => {
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(`${WORKER_URL}${path}`, { ...init, headers });
};

const parse = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const apiFetch = async <T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  let session = loadSession();
  if (session && session.accessExpiresAt < Date.now() + 30_000) {
    try {
      session = await refresh(session);
    } catch {
      handleAuthFailure();
      session = null;
    }
  }
  let res = await sendOnce(path, init, session?.accessToken);
  if (res.status === 401 && session) {
    try {
      session = await refresh(session);
      res = await sendOnce(path, init, session.accessToken);
    } catch {
      handleAuthFailure();
      throw new ApiError('unauthenticated', 401);
    }
  }
  const body = await parse(res);
  if (!res.ok) {
    const code =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'request_failed';
    throw new ApiError(code, res.status, body);
  }
  return body as T;
};

export const apiPost = <T>(path: string, body?: unknown): Promise<T> => {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiFetch<T>(path, init);
};
